import {
  useApiQuery,
  useApiMutation,
  useInfiniteApiQuery,
  queryClient,
  ApiError,
} from '@tasama/api-client-query';
import type {
  NotificationsResponse,
  UnreadCountResponse,
  BackendNotificationData,
  BackendNotificationGroup,
  NotificationAction,
} from '../types/notification-types';

/**
 * Custom hooks for Notification API operations
 * Uses React Query for data fetching with automatic caching and invalidation
 *
 * All hooks use React Query's caching, so multiple components calling the same
 * hook will share the same cached data and only make one API call.
 *
 * Authentication is handled automatically by the API client interceptors.
 */

// ============================================================================
// QUERY HOOKS
// ============================================================================

/**
 * Hook for fetching all notifications with pagination
 * @param limit - Number of notifications to fetch (default: 10)
 * @param offset - Starting offset for pagination (default: 0)
 * @param config - Optional query configuration (e.g., enabled flag)
 */
export const useNotifications = (
  limit: number = 10,
  offset: number = 0,
  config?: { enabled?: boolean }
) => {
  return useApiQuery<NotificationsResponse>(
    ['notifications', 'all', String(limit), String(offset)],
    `/notifications/getAllNotifications?limit=${limit}&offset=${offset}`,
    {
      staleTime: 2 * 60 * 1000, // 2 minutes
      retry: 2,
      retryDelay: 1000,
      enabled: config?.enabled,
    } as any
  );
};

/**
 * Helper to extract groups from page data
 * Handles both wrapped { data: [...] } and direct array response
 */
const getGroupsFromPage = (
  page: NotificationsResponse | BackendNotificationGroup[]
): BackendNotificationGroup[] => {
  if (Array.isArray(page)) {
    return page;
  }
  return page.data ?? [];
};

/**
 * Hook for fetching notifications with infinite scroll pagination
 * Uses React Query's useInfiniteQuery for proper caching and deduplication
 * @param limit - Number of notifications to fetch per page (default: 10)
 */
export const useInfiniteNotifications = (limit: number = 10) => {
  return useInfiniteApiQuery<
    NotificationsResponse | BackendNotificationGroup[],
    NotificationsResponse | BackendNotificationGroup[]
  >(['notifications', 'infinite', String(limit)], {
    getEndpoint: (offset: number) =>
      `/notifications/getAllNotifications?limit=${limit}&offset=${offset}`,
    initialPageParam: 0,
    getNextPageParam: (
      lastPage: NotificationsResponse | BackendNotificationGroup[],
      allPages: (NotificationsResponse | BackendNotificationGroup[])[]
    ): number | undefined => {
      // Calculate total items fetched so far
      const totalFetched = allPages.reduce((sum: number, page) => {
        const groups = getGroupsFromPage(page);
        return (
          sum +
          groups.reduce(
            (acc: number, group: BackendNotificationGroup) =>
              acc + group.notifications.length,
            0
          )
        );
      }, 0);

      // Get last page groups
      const lastPageGroups = getGroupsFromPage(lastPage);
      const lastPageItemCount = lastPageGroups.reduce(
        (acc: number, group: BackendNotificationGroup) =>
          acc + group.notifications.length,
        0
      );

      // Get total count if available (wrapped response), otherwise check if we got less than limit
      const totalCount = !Array.isArray(lastPage)
        ? lastPage.totalCount
        : undefined;

      if (lastPageItemCount < limit) {
        return undefined;
      }
      if (totalCount !== undefined && totalFetched >= totalCount) {
        return undefined;
      }
      return totalFetched;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
    retryDelay: 1000,
  });
};

/**
 * Hook for fetching unread notifications count.
 * Fetches on initial mount and page refresh.
 * Use `invalidateUnreadCount()` or `useApiMutationWithNotificationRefresh` to trigger
 * a refetch after specific API calls.
 * @param config - Optional query configuration (e.g., enabled flag)
 */
export const useUnreadNotificationsCount = (config?: { enabled?: boolean }) => {
  return useApiQuery<UnreadCountResponse>(
    ['notifications', 'unread-count'],
    '/notifications/unread-count',
    {
      staleTime: 1 * 60 * 1000, // 1 minute
      retry: 2,
      retryDelay: 500,
      enabled: config?.enabled,
    } as any
  );
};

// ============================================================================
// NOTIFICATION REFRESH UTILITIES
// ============================================================================

/**
 * Invalidate the unread notifications count query.
 * Call this after any API operation that might affect the notification count.
 *
 * @example
 * // After creating a task
 * await createTask(data);
 * invalidateUnreadCount();
 */
export const invalidateUnreadCount = () => {
  queryClient.invalidateQueries({
    queryKey: ['notifications', 'unread-count'],
  });
};

/**
 * Wrapper hook for mutations that should trigger a notification count refresh.
 * Use this instead of useApiMutation when the mutation might affect notifications.
 *
 * @example
 * const { mutate } = useApiMutationWithNotificationRefresh<ResponseType, RequestType>(
 *   '/tasks/create',
 *   'POST',
 *   { onSuccess: () => console.log('Task created!') }
 * );
 */
export const useApiMutationWithNotificationRefresh = <
  TData = unknown,
  TVariables = unknown,
>(
  endpoint: string | ((_variables: TVariables) => string),
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
  config?: {
    invalidateKeys?: string[];
    onSuccess?: (
      _data: TData,
      _variables: TVariables,
      _context: unknown
    ) => void;
    onError?: (
      _error: ApiError,
      _variables: TVariables,
      _context: unknown
    ) => void;
  }
) => {
  return useApiMutation<TData, TVariables>(endpoint, method, {
    ...config,
    onSuccess: (data, variables, context) => {
      invalidateUnreadCount();
      config?.onSuccess?.(data, variables, context);
    },
  });
};

// ============================================================================
// MUTATION HOOKS
// ============================================================================

/**
 * Hook for marking a specific notification as read
 * Invalidates notifications queries to refresh data
 */
export const useMarkNotificationAsRead = () => {
  return useApiMutation<any, { notificationId: string }>(
    '/notifications/mark-read',
    'POST',
    {
      invalidateKeys: ['notifications'],
      onSuccess: (_data, _variables) => {},
      onError: (_error) => {},
    }
  );
};

/**
 * Hook for marking multiple notifications as read in bulk
 * Invalidates notifications queries to refresh data
 */
export const useMarkNotificationsAsReadBulk = () => {
  return useApiMutation<any, { notificationIds: string[] }>(
    '/notifications/mark-read/',
    'POST',
    {
      invalidateKeys: ['notifications'],
      onSuccess: (_data, _variables) => {},
      onError: (_error) => {},
    }
  );
};

/**
 * Hook for marking all notifications as read
 * Invalidates notifications queries to refresh data
 */
export const useMarkAllNotificationsAsRead = () => {
  return useApiMutation<any, void>('/notifications/mark-all-read', 'POST', {
    invalidateKeys: ['notifications'],
    onSuccess: () => {},
    onError: (_error) => {},
  });
};

// Re-export types for convenience
export type {
  NotificationsResponse,
  UnreadCountResponse,
  BackendNotificationData,
  BackendNotificationGroup,
  NotificationAction,
};
